import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onNavigate, alt }) {
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(Math.min(images.length - 1, index + 1));
      if (e.key === "ArrowLeft") onNavigate(Math.max(0, index - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onNavigate]);

  return createPortal(
    <AnimatePresence>
      {index !== null && (
        <motion.div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-xl flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close photo viewer"
            className="absolute top-5 right-5 w-11 h-11 rounded-full border border-white/25 flex items-center justify-center text-white/70 hover:bg-white hover:text-black transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {images.length > 1 && index > 0 && (
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); onNavigate(index - 1); }}
              aria-label="Previous photo"
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/25 bg-black/50 flex items-center justify-center text-white/80 hover:bg-white hover:text-black transition-colors z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {images.length > 1 && index < images.length - 1 && (
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); onNavigate(index + 1); }}
              aria-label="Next photo"
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/25 bg-black/50 flex items-center justify-center text-white/80 hover:bg-white hover:text-black transition-colors z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          <motion.img
            key={images[index]}
            src={images[index]}
            alt={`${alt} — full size photo ${index + 1}`}
            className="max-h-[85vh] max-w-[92vw] object-contain rounded-sm"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
          />

          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-unbounded text-xs text-white/50">
            {index + 1} / {images.length}
          </p>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
